import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { MessageService } from 'primeng/api';
import { Observable, Subject, tap, withLatestFrom } from 'rxjs';
import { AuthService } from '../auth.service';
import { LoginInvitationApiService } from './services/login-invitation.api.service';

export interface LoginInvitationState {
  step: 'password' | 'pin' | 'otp' | 'tnc' | 'greeting';
  isLoading: boolean;
  errorMessage: string | null;
  token: string | null;
  email: string | null;
  username: string | null;
  merchantName: string | null;
  pin: string | null;
  password: string | null;
  startOtp: boolean;
  isTncSigned: boolean;
  tncContent: string;
  tncTitle: string;
  isSigningTnc: boolean;
  isActivating: boolean;
}

export interface ActivateUser {
  email: string;
  password: string;
  pin: string;
}

export interface ValidateInvitationPayload {
  token: string;
}
export interface TNCMetadata {
  isSigned: boolean;
  title: string;
  activeVersion: string;
  signedVersion: string;
  signedAt: string;
  signedBy: string;
  signedByEmail: string;
  markdown_content: string;
}
export interface ValidateInvitationResponse {
  data: {
    userId: string;
    userName: string;
    email: string;
    merchantName: string;
    merchantId: string;
    tncMetadata?: TNCMetadata;
  };
}
export interface TncSignResponse {
  data: unknown;
}

@Injectable()
export class LoginInvitationStore extends ComponentStore<LoginInvitationState> {
  private readonly tncSigned$ = new Subject<void>();
  readonly onTncSigned$ = this.tncSigned$.asObservable();

  private readonly activated$ = new Subject<void>();
  readonly onActivated$ = this.activated$.asObservable();

  constructor(
    private apiService: LoginInvitationApiService,
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService
  ) {
    super({
      step: 'password',
      isLoading: false,
      errorMessage: null,
      token: '123',
      email: null,
      username: null,
      merchantName: null,
      pin: null,
      password: null,
      startOtp: false,
      isTncSigned: false,
      tncContent: '',
      tncTitle: '',
      isSigningTnc: false,
      isActivating: false,
    });
    this.email$.subscribe(email => {
      if (email) {
        this.authService.setEmail(email);
      }
    });
  }

  readonly step$ = this.select(state => state.step);
  readonly isLoading$ = this.select(state => state.isLoading);
  readonly errorMessage$ = this.select(state => state.errorMessage);
  readonly token$ = this.select(state => state.token);
  readonly pin$ = this.select(state => state.pin);
  readonly startOtp$ = this.select(state => state.startOtp);
  readonly email$ = this.select(state => state.email);
  readonly username$ = this.select(state => state.username);
  readonly merchantName$ = this.select(state => state.merchantName);
  readonly isTncSigned$ = this.select(state => state.isTncSigned);
  readonly tncContent$ = this.select(state => state.tncContent);
  readonly tncTitle$ = this.select(state => state.tncTitle);
  readonly isSigningTnc$ = this.select(state => state.isSigningTnc);
  readonly isActivating$ = this.select(state => state.isActivating);

  readonly setStep = this.updater(
    (state, step: 'password' | 'pin' | 'otp' | 'tnc' | 'greeting') => ({
      ...state,
      step,
    })
  );

  readonly setLoading = this.updater((state, isLoading: boolean) => ({
    ...state,
    isLoading,
  }));

  readonly setToken = this.updater((state, token: string | null) => ({
    ...state,
    token,
  }));

  readonly setEmail = this.updater((state, email: string | null) => ({
    ...state,
    email,
  }));

  readonly setPin = this.updater((state, pin: string | null) => ({
    ...state,
    pin,
  }));

  readonly setPassword = this.updater((state, password: string | null) => ({
    ...state,
    password,
  }));

  readonly setUsername = this.updater((state, username: string | null) => ({
    ...state,
    username,
  }));

  readonly setMerchantName = this.updater((state, merchantName: string | null) => ({
    ...state,
    merchantName,
  }));

  readonly setErrorMessage = this.updater((state, errorMessage: string | null) => ({
    ...state,
    errorMessage,
  }));

  readonly setStartOtp = this.updater((state, startOtp: boolean) => ({
    ...state,
    startOtp,
  }));

  readonly setIsTncSigned = this.updater((state, isTncSigned: boolean) => ({
    ...state,
    isTncSigned,
  }));

  readonly setTncContent = this.updater((state, tncContent: string) => ({
    ...state,
    tncContent,
  }));

  readonly setTncTitle = this.updater((state, tncTitle: string) => ({
    ...state,
    tncTitle,
  }));

  readonly setActivating = this.updater((state, isActivating: boolean) => ({
    ...state,
    isActivating,
  }));

  readonly setSigningTnc = this.updater((state, isSigningTnc: boolean) => ({
    ...state,
    isSigningTnc,
  }));

  readonly verifyLinkInvitation = this.effect((payload$: Observable<ValidateInvitationPayload>) =>
    payload$.pipe(
      tap(() => this.setLoading(true)),
      tap(token => {
        this.apiService
          .verifyLinkInvitation(token)
          .pipe(
            tap({
              next: res => {
                this.setEmail(res.data.email);
                this.setUsername(res.data.userName);
                this.setMerchantName(res.data.merchantName);
                this.setIsTncSigned(res.data.tncMetadata?.isSigned ?? false);
                this.setTncContent(res.data.tncMetadata?.markdown_content ?? '');
                this.setTncTitle(res.data.tncMetadata?.title ?? 'PFP-772-C');
                this.setLoading(false);
                this.setErrorMessage(null);
              },
              error: error => {
                this.setLoading(false);
                this.checkAccessToken();
                this.setErrorMessage(error.message);
              },
            })
          )
          .subscribe();
      })
    )
  );

  readonly activateUser = this.effect((payload$: Observable<void>) =>
    payload$.pipe(
      withLatestFrom(this.state$),
      tap(([, state]) => {
        const { token, email, password, pin } = state;
        const activateToken = this.authService.getTempToken() ?? '';
        if (!token || !email || !password || !pin) {
          this.setErrorMessage('Missing required fields');
          return;
        }
        this.setLoading(true);
        this.setActivating(true);
        const payload: ActivateUser = { email, password, pin };
        this.apiService
          .activateUser(payload, token, activateToken)
          .pipe(
            tap({
              next: res => {
                this.setLoading(false);
                this.setActivating(false);
                this.setErrorMessage(null);
                this.authService.setUserInfo(res.data.userInfo);
                this.authService.setAccessToken(res.data.accessToken);
                this.activated$.next();
              },
              error: error => {
                this.setLoading(false);
                this.setActivating(false);
                this.setErrorMessage(error.message);
              },
            })
          )
          .subscribe();
      })
    )
  );

  readonly signTnc = this.effect((payload$: Observable<void>) =>
    payload$.pipe(
      withLatestFrom(this.state$),
      tap(([, state]) => {
        // skip request tnc if the content is nto exist and current state is not signed, also in process of signing doc
        if (state.isSigningTnc || (!state.isTncSigned && !state.tncContent)) {
          return;
        }
        const accessToken = this.authService.getAccessToken();
        if (!accessToken) {
          this.setErrorMessage('Missing access token');
          return;
        }
        this.setSigningTnc(true);
        this.apiService
          .signTnc(accessToken)
          .pipe(
            tap({
              next: () => {
                this.setSigningTnc(false);
                this.setIsTncSigned(true);
                this.tncSigned$.next();
              },
              error: error => {
                this.setSigningTnc(false);
                this.setErrorMessage(error.message);
                this.messageService.add({
                  key: 'message',
                  severity: 'error',
                  data: {
                    title: '',
                    body: error?.message ?? 'Failed to sign Terms & Conditions. Please try again.',
                  },
                });
              },
            })
          )
          .subscribe();
      })
    )
  );

  checkAccessToken() {
    if (this.authService.getAccessToken()) {
      this.router.navigateByUrl('/');
    } else {
      this.router.navigateByUrl('/login');
    }
  }

  clearLocalStorage() {
    localStorage.clear();
  }
}
